/**
 * Encapsulates the games POS status payload and provides a clean row format for the UI.
 */

export interface GamesPosStatusGameServerData {
  game_id: string;
  game_name: string;
  plu: string;
  price_per_ticket: number | string;
  tickets_per_pack: number | string;
  added_to_pos: boolean;
}

export interface GamesPosStatusServerData {
  store_id: string;
  state?: string | null;
  count: number;
  games: GamesPosStatusGameServerData[];
}

export interface GamesPosStatusGameRow {
  gameName: string;
  gameNumber: string;
  upc: string;
  packSize: string;
  price: string;
  addedToPos: boolean;
}

class GamesPosStatusGameModel {
  private _serverData: GamesPosStatusGameServerData;

  constructor(serverData: GamesPosStatusGameServerData) {
    this._serverData = serverData;
  }

  get game_id() {
    return this._serverData.game_id ?? '';
  }

  get game_name() {
    return this._serverData.game_name ?? '';
  }

  get plu() {
    return this._serverData.plu ?? '';
  }

  get price_per_ticket() {
    return this._serverData.price_per_ticket ?? 0;
  }

  get tickets_per_pack() {
    return this._serverData.tickets_per_pack ?? 0;
  }

  get added_to_pos() {
    return Boolean(this._serverData.added_to_pos);
  }

  toRowFormat(): GamesPosStatusGameRow {
    return {
      gameName: this.game_name || '-',
      gameNumber: this.game_id || '-',
      upc: this.plu || '-',
      packSize: String(this.tickets_per_pack ?? '-'),
      price: Number(this.price_per_ticket ?? 0).toFixed(2),
      addedToPos: this.added_to_pos,
    };
  }

  getRawData() {
    return this._serverData;
  }

  static fromServerData(serverData: GamesPosStatusGameServerData) {
    return new GamesPosStatusGameModel(serverData);
  }
}

class GamesPosStatusModel {
  private _serverData: GamesPosStatusServerData | null;
  private _games: GamesPosStatusGameModel[];

  constructor(serverData: GamesPosStatusServerData | null = null) {
    this._serverData = serverData;
    this._games = (serverData?.games || []).map((game) => GamesPosStatusGameModel.fromServerData(game));
  }

  get store_id() {
    return this._serverData?.store_id ?? '';
  }

  get state() {
    return this._serverData?.state ?? null;
  }

  get count() {
    return this._serverData?.count ?? 0;
  }

  get games() {
    return this._games.map((game) => game.toRowFormat());
  }

  getRawData() {
    return this._serverData;
  }

  static fromServerData(serverData: GamesPosStatusServerData | null | undefined) {
    return new GamesPosStatusModel(serverData ?? null);
  }

  static createEmpty() {
    return new GamesPosStatusModel(null);
  }
}

export default GamesPosStatusModel;
export { GamesPosStatusGameModel };
